import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormRow } from '@gravity-ui/components';
import { TrashBin } from '@gravity-ui/icons';
import type { RadioButtonOption } from '@gravity-ui/uikit';
import { Button, RadioButton, TextArea } from '@gravity-ui/uikit';

import { useGetSectionsQuery } from 'store/api';
import type { Section } from 'store/types/common';

import { DeleteModal } from '../delete-modal';
import { SectionsSelect } from './sections-select';
import { SubsectionsSelect } from './subsections-select';
import { GradeSelect } from './grade-select';
import { ShortTermGoals } from './short-term-goals';

import { GRADE, PROBLEM_DESCRIPTION, TITLE_DESCRIPTION } from '../../../constants';

import styles from './styles.module.scss';

export const ProblemBlock = ({ index, removeBlock, methods, errors, isAdaptive, isMobile }: any) => {
	const [open, setOpen] = useState(false);
	const [selectedSection, setSelectedSection] = useState<string | null>(null);
	const [filteredSubsections, setFilteredSubsections] = useState<Section[]>([]);

	const { data: sections } = useGetSectionsQuery();

	const {
		register,
		setValue,
		watch,
		control,
		formState: { isSubmitted },
	} = methods;

	const formValues = watch();

	useEffect(() => {
		if (selectedSection && sections) {
			const selectedSectionObject = sections.find((section: Section) => section.sectionName === selectedSection);
			if (selectedSectionObject) {
				setFilteredSubsections(selectedSectionObject.subsections || []);
			} else {
				setFilteredSubsections([]);
			}
		} else {
			setFilteredSubsections([]);
		}
	}, [selectedSection, sections]);

	const ratingOptions: RadioButtonOption[] = Array.from({ length: 10 }, (_, i) => ({
		value: String(i + 1),
		content: String(i + 1),
	}));

	const sectionsOptions = sections?.map((section: Section) => ({
		value: section.sectionName,
		content: section.sectionName,
	}));

	const subsectionsOptions = filteredSubsections.map((subsection: Section) => ({
		value: subsection.sectionName,
		content: subsection.sectionName,
	}));

	const ratingNumbers = Array.from({ length: 10 }, (_, i) => i + 1);

	return (
		<>
			{sections && (
				<div className={styles.block} key={index}>
					<div className={styles.block__delete}>
						<h4>{TITLE_DESCRIPTION}</h4>

						<Button onClick={() => setOpen(true)} view="flat">
							<TrashBin />
						</Button>
						<DeleteModal open={open} setOpen={setOpen} removeBlock={removeBlock} index={index} />
					</div>

					<SectionsSelect
						control={control}
						index={index}
						sectionsOptions={sectionsOptions}
						formValues={formValues}
						register={register}
						setSelectedSection={setSelectedSection}
						setValue={setValue}
						errors={errors}
						isMobile={isMobile}
					/>

					<SubsectionsSelect
						control={control}
						index={index}
						subsectionsOptions={subsectionsOptions}
						formValues={formValues}
						register={register}
						setValue={setValue}
						errors={errors}
						isMobile={isMobile}
					/>

					<FormRow label={PROBLEM_DESCRIPTION} className={styles.row} required>
						<Controller
							name={`problems.${index}.text`}
							control={control}
							rules={{ required: true }}
							render={({ field, fieldState }) => (
								<TextArea
									value={field.value || ''}
									onChange={(event) => {
										field.onChange(event);
									}}
									validationState={fieldState.error ? 'invalid' : undefined}
									errorPlacement="inside"
									errorMessage={
										errors?.problems?.[index]?.text ? 'Обязательно для заполнения' : undefined
									}
									minRows={4}
									note={<span>{(field.value || '').length}/200</span>}
									hasClear
								/>
							)}
						/>
					</FormRow>

					<FormRow label="Детальное описание проблемы (NOW)" className={styles.row}>
						<Controller
							name={`problems.${index}.now`}
							control={control}
							render={({ field, fieldState }) => (
								<TextArea
									value={field.value || ''}
									onChange={(event) => {
										field.onChange(event);
									}}
									validationState={fieldState.error ? 'invalid' : undefined}
									errorPlacement="inside"
									errorMessage={fieldState.error?.message}
									minRows={4}
									note={<span>{(field.value || '').length}/2000</span>}
									hasClear
									placeholder="Очень детализированно опишите точку входа, саму проблему подробно"
								/>
							)}
						/>
					</FormRow>

					{isAdaptive ? (
						<GradeSelect
							isMobile={isMobile}
							formValues={formValues}
							index={index}
							register={register}
							setValue={setValue}
							errors={errors}
							ratingNumbers={ratingNumbers}
						/>
					) : (
						<>
							{Array.isArray(ratingOptions) && (
								<FormRow label={GRADE} className={`${styles.row} ${styles.radio_btn}`} required>
									<Controller
										name={`problems.${index}.importance`}
										control={control}
										rules={{
											required: 'Обязательно для заполнения',
										}}
										render={({ field, fieldState }) => {
											return (
												<div>
													<RadioButton
														name="importance-radio-button"
														options={ratingOptions}
														value={field.value ? String(field.value) : ''}
														onUpdate={(val) => field.onChange(Number(val))}
														size="l"
													/>

													{fieldState.error && (
														<div className={styles.errorMessage}>Выберите значение</div>
													)}
												</div>
											);
										}}
									/>
								</FormRow>
							)}
						</>
					)}

					<ShortTermGoals
						index={index}
						control={control}
						register={register}
						errors={errors}
					/>
				</div>
			)}
		</>
	);
};
